import React from "react";
import { Link } from "react-router-dom";
import {
  Search,
  PenTool,
  Code2,
  Rocket,
  ArrowRight,
} from "lucide-react";

const steps = [
  {
    no: "01",
    icon: Search,
    title: "Discover",
    desc: "Understand your business goals and project requirements.",
  },
  {
    no: "02",
    icon: PenTool,
    title: "Design",
    desc: "Create intuitive UI/UX with seamless user experiences.",
  },
  {
    no: "03",
    icon: Code2,
    title: "Develop",
    desc: "Build scalable and secure web & mobile applications.",
  },
  {
    no: "04",
    icon: Rocket,
    title: "Launch",
    desc: "Deploy, monitor and support your product long-term.",
  },
];

export default function AboutAndProcess() {
  return (
    <section
      id="process"
      className="
        relative
        scroll-mt-24
        overflow-hidden
        bg-zinc-50
        py-12
        sm:py-16
        lg:py-24
      "
    >
      {/* Background Glow */}
      <div
        className="
          pointer-events-none
          absolute
          -left-20
          top-0
          h-64
          w-64
          rounded-full
          bg-orange-500/[0.04]
          blur-[80px]

          sm:left-1/4
          sm:h-[400px]
          sm:w-[400px]
          sm:-translate-x-1/2
          sm:blur-[120px]

          lg:h-[600px]
          lg:w-[600px]
          lg:blur-[140px]
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          -right-20
          bottom-0
          h-64
          w-64
          rounded-full
          bg-orange-500/[0.04]
          blur-[80px]

          sm:right-1/4
          sm:h-[400px]
          sm:w-[400px]
          sm:translate-x-1/2
          sm:blur-[120px]

          lg:h-[600px]
          lg:w-[600px]
          lg:blur-[140px]
        "
      />

      <div
        className="
          relative
          mx-auto
          w-full
          max-w-7xl
          px-4
          sm:px-6
          lg:px-8
        "
      >
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center">
          <p
            className="
              text-[10px]
              font-bold
              uppercase
              tracking-[0.2em]
              text-orange-600

              sm:text-xs
              sm:tracking-widest
            "
          >
            About Us
          </p>

          <h2
            className="
              mx-auto
              mt-3
              max-w-[340px]
              text-[28px]
              font-bold
              leading-[1.15]
              tracking-tight
              text-zinc-900

              sm:max-w-xl
              sm:text-4xl

              md:text-5xl
            "
          >
            A studio that ships,{" "}
            <span className="font-medium italic text-orange-600">
              not just designs
            </span>
          </h2>

          <p
            className="
              mx-auto
              mt-4
              max-w-[350px]
              text-sm
              leading-6
              text-zinc-600

              sm:max-w-2xl
              sm:text-base
              sm:leading-7
            "
          >
            We're a software studio helping startups and growing businesses
            turn ideas into reliable, scalable digital products — from strategy
            to launch and beyond.
          </p>
        </div>

        {/* Steps Grid */}
        <div
          className="
            mx-auto
            mt-8
            max-w-2xl

            sm:mt-10

            lg:max-w-none
            lg:mt-12
          "
        >
          <div
            className="
              grid
              grid-cols-2
              gap-3

              sm:gap-5

              lg:grid-cols-4
              lg:gap-6
            "
          >
            {steps.map((step) => {
              const Icon = step.icon;

              return (
                <div
                  key={step.no}
                  className="
                    group
                    relative
                    min-w-0
                    rounded-xl
                    border
                    border-zinc-200
                    bg-white
                    p-4
                    transition-all
                    duration-300

                    sm:rounded-2xl
                    sm:p-6

                    lg:p-8
                    lg:hover:-translate-y-1
                    lg:hover:border-orange-500/50
                    lg:hover:shadow-[0_20px_50px_rgba(249,115,22,0.08)]
                  "
                >
                  {/* Icon and Step Number */}
                  <div className="flex items-center justify-between gap-2">
                    <div
                      className="
                        flex
                        h-10
                        w-10
                        shrink-0
                        items-center
                        justify-center
                        rounded-lg
                        border
                        border-zinc-100
                        bg-zinc-50
                        text-orange-600
                        transition-colors
                        duration-300

                        sm:h-12
                        sm:w-12
                        sm:rounded-xl

                        lg:group-hover:bg-orange-600
                        lg:group-hover:text-white
                      "
                    >
                      <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                    </div>

                    <span
                      className="
                        select-none
                        text-2xl
                        font-black
                        text-zinc-100
                        transition-colors
                        duration-300

                        sm:text-3xl

                        lg:text-4xl
                        lg:group-hover:text-orange-500/10
                      "
                    >
                      {step.no}
                    </span>
                  </div>

                  {/* Content */}
                  <h3
                    className="
                      mt-4
                      text-base
                      font-semibold
                      tracking-tight
                      text-zinc-900

                      sm:mt-5
                      sm:text-lg

                      lg:mt-6
                      lg:text-xl
                    "
                  >
                    {step.title}
                  </h3>

                  <p
                    className="
                      mt-2
                      text-xs
                      leading-5
                      text-zinc-600

                      sm:mt-3
                      sm:text-sm
                      sm:leading-6
                    "
                  >
                    {step.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 text-center sm:mt-10 lg:mt-14">
          <Link
            to="/about"
            className="
              inline-flex
              items-center
              justify-center
              gap-2
              rounded-full
              bg-orange-600
              px-6
              py-3
              text-sm
              font-semibold
              text-white
              shadow-sm
              transition-all
              duration-200

              hover:bg-orange-500
              hover:shadow-[0_8px_25px_rgba(249,115,22,0.25)]

              lg:hover:-translate-y-0.5
            "
          >
            Explore Us
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}